<template>
	<div :class="{ panel: true, section: isSection }">
		<div class="panel-header">
			<div class="flex-side-by-side">
				<button
					v-if="util.hasKey(props, 'collapsed')"
					class="button-span panel-header-collapse-button"
					@click="onCollapse()"
				>
					<i :class="['far', isCollapsed ? 'fa-plus-square' : 'fa-minus-square']"></i>
				</button>

				<div class="panel-header-title">{{ title }}</div>
				<slot name="header"></slot>
				<div
					v-if="subtext && !Array.isArray(subtext)"
					class="panel-header-subtext"
				>
					<span
						v-if="subtext.label"
						class="panel-header-subtext-label"
						>{{ subtext.label }}:&nbsp;</span
					>
					<span
						v-if="subtext.value"
						class="panel-header-subtext-value"
						>{{ subtext.value }}</span
					>
				</div>
				<div
					v-if="subtext && Array.isArray(subtext)"
					class="panel-header-subtext-array"
				>
					<div
						v-for="s in subtext"
						:key="s.$index"
						class="panel-header-subtext-item"
					>
						<span
							v-if="s.label"
							class="panel-header-subtext-label"
							>{{ s.label }}:&nbsp;</span
						>
						<span
							v-if="s.value"
							class="panel-header-subtext-value"
							>{{ s.value }}</span
						>
					</div>
				</div>
			</div>

			<div
				v-if="action && !Array.isArray(action)"
				class="panel-header-button"
			>
				<template v-if="!action.isHidden">
					<button
						:class="action.class"
						:disabled="action.disabled"
						@click="action.onClick"
						:type="action.type"
						:title="action.title"
					>
						<span v-if="action.icon"><i :class="action.icon"></i></span>
						<span v-if="action.text">{{ action.text }}</span>
					</button>
				</template>
			</div>
			<div
				v-else-if="action && action.length"
				class="panel-header-button-array"
			>
				<button
					v-for="button in filteredActionList"
					:key="button.$index"
					:class="button.class"
					:disabled="button.disabled"
					@click="button.onClick"
					:type="button.type"
					:title="button.title"
				>
					<span v-if="button.icon"
						><i
							class="far"
							:class="button.icon"
						></i
					></span>
					<span v-if="button.text">{{ button.text }}</span>
				</button>
			</div>

			<div
				v-if="util.hasKey(slots, 'HeaderComponent')"
				class="header-component"
			>
				<slot name="HeaderComponent"></slot>
			</div>
		</div>

		<div
			v-show="!isCollapsed"
			class="panel-body"
		>
			<slot></slot>
		</div>
	</div>
</template>

<script lang="ts">
	export interface IPanelProps {
		/** Main title of the panel */
		title?: string;

		/** Displays key-values in header; Expects object (or array of objects) in the form of { label: '', value: '' } */
		subtext?: any | any[];

		/** Header buttons; Expects object (or array of objects) in the form of { class: '', icon: '', text: '', disabled: false, onClick: () => {} } */
		action?: any | any[];

		/** Controls whether or not to collapse the panel body (providing this prop makes the collapse button visible) */
		collapsed?: boolean | null;

		isSection?: boolean;
	}
</script>
<script setup lang="ts">
	import { computed, ref, toRefs, useSlots, watch } from 'vue';
	import util from '@core/services/util';

	const slots = useSlots();
	const props = withDefaults(defineProps<IPanelProps>(), {
		collapsed: undefined,
	});
	const { action, collapsed, isSection, subtext, title } = toRefs(props);

	const emit = defineEmits({
		collapse(currentState: boolean) {
			return typeof currentState === 'boolean';
		},
	});

	const filteredActionList = computed(() => {
		return action.value.filter((b: any) => !b.isHidden);
	});

	// This had null before. If its undefined we're setting it back to null; so it maintain whatever logic we had before
	const isCollapsed = ref<boolean>(props.collapsed ?? null);

	watch(
		() => collapsed.value,
		newVal => {
			setIsCollapsed(newVal);
		},
	);

	const setIsCollapsed = (newVal: boolean) => {
		isCollapsed.value = newVal;
		window.dispatchEvent(new Event('resize'));
	};

	const onCollapse = () => {
		isCollapsed.value = !isCollapsed.value;
		emit('collapse', isCollapsed.value);

		window.dispatchEvent(new Event('resize'));
	};
</script>
